/**
 * Componente contenedor de mapas para Forecast Metrics
 * Maneja el renderizado de mapas izquierdo y derecho con sus respectivos overlays
 */

import React from 'react';
import { Grid, Paper, Box, Stack, Typography } from '@mui/material';
import MapLeft from './maps/map_left.js';
import MapRight from './maps/map_right.js';

/**
 * Contenedor de mapas para la vista Forecast Metrics
 * @param {Object} props - Propiedades del componente
 * @returns {JSX.Element} Componente de contenedor de mapas
 */
const ForecastMapContainer = ({
  prettyModelName,
  issueTime,
  cqlSevere,
  cqlFecha,
  mapLeftLayer,
  weekNumber,
  selectedTime,
  mapRightLayer,
  selectedModel,
  metricsName,
}) => {
  // Nombres legibles
  const LAYER_PRETTY = {
    'CNN_HISTORICAL:EU_025_live_demo': 'Convection (~28km)',
    'CNN_HISTORICAL:EU_01_live_demo': 'Convection (~9km)',
    'CNN_HISTORICAL:euro_1k_00625_15m': 'Convection (~6km)',
    'TURBULENCE_HISTORICAL:turbulence_historical': 'Turbulence (~14km)',
    'VISIBILITY:visibility_historical': 'Visibility',
    'DUST_HISTORICAL:dust_historical': 'Dust (~28km)',
    'ICE_HISTORICAL:ice_historical': 'HAIC',
    // por si en algún momento usas las "live"
    'CNN:EU_025_live_demo': 'Convection',
    'CNN:EU_01_live_demo': 'Convection High Resolution',
  };

  const leftLabel = `AI ${LAYER_PRETTY[mapLeftLayer] ?? mapLeftLayer}`;
  // Sin capa de ground truth (turbulence, visibility, dust) → un único mapa a pantalla completa
  const singleMap = !mapRightLayer;
  const handleRiskClick = (risk) => {
    console.log("Riesgo seleccionado en el contenedor:", risk);
    // Aquí puedes añadir lógica extra si quieres que pase algo al hacer clic
  };
  return (
    <Box sx={{
      display: 'flex',
      flexDirection: 'column',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      width: '100%',
      height: 'auto',
      overflowY: { xs: 'auto', md: 'hidden' },
      overflowX: 'hidden',
      minHeight: 0,
      
      paddingBottom:'80px'
    }}>
      <Grid
        container
        sx={{
          height: singleMap ? '100%' : { xs: 'auto', md: '100%' },
          flex: singleMap ? 1 : { xs: '0 0 auto', md: 1 },

          minHeight: 0,
          margin: 0,
          '& > MuiGrid-Item': { paddingTop: 0, paddingBottom: 0 }
        }}
      >
        {/* Mapa Izquierdo - Forecast */}
        <Grid
          item
          xs={12}
          md={singleMap ? 12 : 6}
          sx={{
           
            height: singleMap ? '100%' : { xs: '50vh', md: '100%' },
            display: 'flex',
            flexDirection: 'column',
            width: '100%',
            minHeight: 0
          }}
        >
          <Paper
            elevation={2}
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              zIndex: 0,
              minHeight: 0,
              height: '100%'
            }}
          >
            <Box sx={{ position: 'relative', width: '100%', height: '100%', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
              <Box
                sx={{
                  position: 'absolute',
                  top: 8,
                  left: 0,
                  right: 0,
                  zIndex: 2, // por encima del aviso de carga del mapa (MapLoadingOverlay, zIndex 1)
                  textAlign: 'center',
                  pointerEvents: 'none', // no bloquea el mapa
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  sx={{
                    display: 'inline-flex',
                    background: 'linear-gradient(135deg, #16a34a 0%, #22c55e 52%, #4ade80 100%)',
                    borderRadius: '20px',
                    px: 2.2,
                    py: 0.65,
                    boxShadow: '0 3px 14px rgba(34,197,94,0.35), inset 0 1px 0 rgba(255,255,255,0.18)',
                    border: '1px solid rgba(255,255,255,0.14)',
                    backdropFilter: 'blur(4px)',
                    transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      transform: 'translateY(-1px)',
                      boxShadow: '0 5px 18px rgba(34,197,94,0.45)',
                    },
                  }}
                >
                  <Typography sx={{
                    color: '#fff',
                    fontSize: '0.75rem',
                    fontWeight: 600,
                    fontFamily: '"Open Sans", sans-serif',
                    letterSpacing: '0.03em',
                    lineHeight: 1,
                    whiteSpace: 'nowrap',
                    textTransform: 'none',
                  }}>
                    {leftLabel}
                  </Typography>
                </Stack>
              </Box>
              <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
                <MapLeft
                  cqlSevere={cqlSevere}
                  cqlFecha={cqlFecha}
                  mapLeftLayer={mapLeftLayer}
                  weekNumber={weekNumber}
                  issueTime={issueTime}
                  selectedTime={selectedTime}
                  metricsName={metricsName}
                  handleRiskClick={handleRiskClick}
                  label={leftLabel}
                />
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Mapa Derecho - Ground Truth */}
        {!singleMap && (
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            height: { xs: '50vh', md: '100%' },
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
            
            
          }}
        >
          <Paper
            elevation={2}
            sx={{ position: 'relative', height: '100%', zIndex: 0, width: '100%', minHeight: 0, display: 'flex', flexDirection: 'column' }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: 8,
                left: 0,
                right: 0,
                zIndex: 2, // por encima del aviso de carga del mapa (MapLoadingOverlay, zIndex 1)
                textAlign: 'center',
                pointerEvents: 'none'
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                sx={{
                  display: 'inline-flex',
                  background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 52%, #60a5fa 100%)',
                  borderRadius: '20px',
                  px: 2.2,
                  py: 0.65,
                  boxShadow: '0 3px 14px rgba(59,130,246,0.35), inset 0 1px 0 rgba(255,255,255,0.18)',
                  border: '1px solid rgba(255,255,255,0.14)',
                  backdropFilter: 'blur(4px)',
                  transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                  '&:hover': {
                    transform: 'translateY(-1px)',
                    boxShadow: '0 5px 18px rgba(59,130,246,0.45)',
                  },
                }}
              >
                <Typography sx={{
                  color: '#fff',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                  fontFamily: '"Open Sans", sans-serif',
                  letterSpacing: '0.03em',
                  lineHeight: 1,
                  whiteSpace: 'nowrap',
                  textTransform: 'none',
                }}>
                  Ground Truth
                </Typography>
              </Stack>
            </Box>
            <Box sx={{ flex: 1, minHeight: 0, position: 'relative' }}>
              <MapRight
                cqlSevere={cqlSevere}
                cqlFecha={cqlFecha}
                mapRightLayer={mapRightLayer}
                weekNumber={weekNumber}
                issueTime={issueTime}
                selectedModel={selectedModel}
                isInteractive={true}
                metricsNames={metricsName}
              />
            </Box>
          </Paper>
        </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default ForecastMapContainer;
